import { lazy } from 'react';

// Lazy-import helpers that auto-recover from stale-shell-vs-new-build
// hash mismatches.
//
// The failure mode: a user keeps the SPA open while we ship a new build.
// Their in-memory shell still references the OLD hashed chunk
// (QrScanner-DqAHA4qB.js), but the deploy replaced it with a new hash
// (QrScanner-BCnzktwH.js). The moment something triggers the dynamic
// import — for us, opening "Add a device" — the fetch 404s and React
// throws `error loading dynamically imported module: …`.
//
// ErrorBoundary catches it and tells the user to reload. Better: notice
// the specific error class and reload AUTOMATICALLY, so the user never
// sees the crash screen at all. Vite's official guidance for this case.
//
// Guarded against infinite reload loops via a sessionStorage flag: if we
// already auto-reloaded once this tab and the error reoccurs (which would
// mean the new build is itself busted, not a stale-hash race), we let the
// error propagate so ErrorBoundary takes over and the user sees a real
// crash surface.

const RELOAD_FLAG = 'aqs.chunk-reload-at';
const RELOAD_GRACE_MS = 30_000; // any chunk error within this window after a reload → propagate

function isChunkLoadError(err) {
  const msg = String(err?.message || err || '');
  return (
    /dynamically imported module/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
}

function shouldRetryReload() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    if (!last) return true;
    // If our previous reload was very recent, the new shell is broken too —
    // give up and let the boundary show.
    return Date.now() - last > RELOAD_GRACE_MS;
  } catch {
    return true;
  }
}

function markReload() {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* private mode etc — best effort */
  }
}

async function withChunkRecovery(factory) {
  try {
    const mod = await factory();
    // First successful load this tab — wipe the flag so a future real
    // chunk error can trigger another recovery.
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* ignore */
    }
    return mod;
  } catch (e) {
    if (isChunkLoadError(e) && shouldRetryReload()) {
      markReload();
      window.location.reload();
      // Return a never-resolving promise so React doesn't try to render
      // a placeholder while the page is tearing down.
      return new Promise(() => {});
    }
    throw e;
  }
}

// Drop-in replacement for React.lazy(() => import('…')).
export function lazyWithRefresh(factory) {
  return lazy(() => withChunkRecovery(factory));
}

// Drop-in replacement for a plain dynamic `import()` you use outside of
// React.lazy (e.g. you import inside an event handler to access a non-
// component export).
export function dynamicImportWithRefresh(factory) {
  return withChunkRecovery(factory);
}
